import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

export default function Materials() {
  const { hasPermission } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  const load = () => {
    setLoading(true);
    get('/materials').then(d => setMaterials(d.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); get('/suppliers').then(d => setSuppliers(d.data)); }, []);

  const low = materials.filter(m => m.is_low_stock);

  return (
    <div>
      <div className="flex-between mb-lg">
        <h2 className="page-title">Raw Materials</h2>
        {hasPermission('materials:write') && (
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Add Material</button>
        )}
      </div>
      {low.length > 0 && (
        <div className="alert alert-warn mb-lg">
          Low stock: <strong>{low.map(m=>m.name).join(', ')}</strong>
        </div>
      )}
      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead><tr style={{background:'#556B2F'}}>
              <th style={{color:'#F2EFE7'}}>Material</th><th style={{color:'#F2EFE7'}}>Location</th><th style={{color:'#F2EFE7'}}>Unit</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Stock</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Reorder</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Avg Cost</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Value</th>
              <th></th>
            </tr></thead>
            <tbody>
              {materials.length === 0
                ? <tr><td colSpan="8"><div className="empty-state"><h3>No materials yet</h3></div></td></tr>
                : materials.map(m => (
                  <tr key={m.id} className={m.is_low_stock ? 'low-stock-row' : ''}>
                    <td style={{fontWeight:500}}>{m.name}
                      {m.is_low_stock && <span className="badge b-part" style={{marginLeft:8,fontSize:9}}>Low</span>}
                    </td>
                    <td>{m.storage_location || 'Not assigned'}</td>
                    <td>{m.unit_of_measure}</td>
                    <td className="amt">{Number(m.current_stock).toLocaleString()}</td>
                    <td className="amt">{Number(m.reorder_level).toLocaleString()}</td>
                    <td className="amt">{fmt(m.weighted_avg_cost)}</td>
                    <td className="amt">{fmt(m.current_stock * m.weighted_avg_cost)}</td>
                    <td>
                      <div className="flex-center gap-sm">
                        {hasPermission('inventory:write') && (
                          <button className="btn btn-gold btn-sm" onClick={() => setModal({...m, _purchase:true})}>+ Stock</button>
                        )}
                        {hasPermission('materials:delete') && (
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          if (!window.confirm('Delete '+m.name+'? This cannot be undone.')) return;
                          try { await del('/materials/'+m.id); load(); }
                          catch (err) { alert(err.message); }
                        }}>Delete</button>
                      )}
                      {hasPermission('materials:write') && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setModal(m)}>Edit</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>
      {modal !== null && !modal._purchase && (
        <MaterialModal material={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?._purchase && (
        <PurchaseModal material={modal} suppliers={suppliers} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function MaterialModal({ material, onClose, onSaved }) {
  const isEdit = material?.id;
  const [form, setForm] = useState(material || { name:'',unit_of_measure:'',reorder_level:0,weighted_avg_cost:0,current_stock:0,storage_location:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!form.name) { setError('Name required'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) await put('/materials/'+material.id, form);
      else await post('/materials', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay"><div className="modal">
      <div className="modal-hdr">
        <span className="modal-title">{isEdit?'Edit':'New'} Material</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">
        {error && <div className="alert alert-error mb-md">{error}</div>}
        <div className="form-group"><label className="form-label">Name *</label>
          <input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
        <div className="form-group">
          <label className="form-label">Storage Location</label>
          <input
            className="form-control"
            placeholder="Example: Store A, Rack 2, Shelf B"
            value={form.storage_location || ''}
            onChange={e=>setForm({
              ...form,
              storage_location:e.target.value
            })}
          />
          <div className="form-hint">
            Physical location of this parent material in the store
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Unit *</label>
            <input className="form-control" placeholder="metres, kg, rolls..." value={form.unit_of_measure||''} onChange={e=>setForm({...form,unit_of_measure:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Current Stock</label>
            <input
              type="number"
              className="form-control"
              value={form.current_stock || 0}
              readOnly
              disabled
            />
            <div className="form-hint">
              Stock changes only through controlled Inventory transactions
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Reorder Level</label>
            <input type="number" className="form-control" value={form.reorder_level||0} onChange={e=>setForm({...form,reorder_level:e.target.value})} /></div>
          <div className="form-group">
            <label className="form-label">Unit Cost / Weighted Avg Cost (N)</label>
            <input
              type="number"
              className="form-control"
              value={form.weighted_avg_cost || 0}
              readOnly
              disabled
            />
            <div className="form-hint">
              Updated automatically through controlled Inventory receipts
            </div>
          </div>
        </div>
      </div>
      <div className="modal-ftr">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving?'Saving...':'Save'}</button>
      </div>
    </div></div>
  );
}

function PurchaseModal({ material, suppliers, onClose, onSaved }) {
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], quantity:'', unit_cost: material.weighted_avg_cost||'', supplier_id:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await post('/materials/'+material.id+'/purchase', form); onSaved(); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay"><div className="modal">
      <div className="modal-hdr">
        <span className="modal-title">Stock Purchase — {material.name}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">
        <div className="alert alert-info mb-md">
          Current stock: <strong>{Number(material.current_stock).toLocaleString()} {material.unit_of_measure}</strong>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Date *</label>
            <input type="date" className="form-control" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Quantity *</label>
            <input type="number" className="form-control" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Unit Cost (N) *</label>
            <input type="number" className="form-control" value={form.unit_cost} onChange={e=>setForm({...form,unit_cost:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Supplier</label>
            <select className="form-control" value={form.supplier_id} onChange={e=>setForm({...form,supplier_id:e.target.value})}>
              <option value="">Select supplier</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
        </div>
      </div>
      <div className="modal-ftr">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving?'Saving...':'Record Purchase'}</button>
      </div>
    </div></div>
  );
}
